/**
 * useDashboard 훅
 *
 * 책임:
 * - 3개 API를 병렬로 호출 (KPI, 히트맵, 모듈 헬스)
 * - 로딩/에러/데이터 상태 관리
 * - 재시도 기능
 *
 * 히트맵 응답이 각 셀에 실행 정보(run)를 임베드하므로,
 * 예전처럼 모듈별 /jobs 조회나 개별 /jobs/{id} 상세 조회가 필요 없음.
 *
 * 편성표는 날짜 이동을 위해 별도 훅(useSchedule)이 전담한다.
 *
 * DashboardContainer에서 사용
 */

import { useEffect, useState, useCallback } from 'react';
import * as endpoints from '../api/endpoints';
import type {
  DashboardKpiResponse,
  HeatmapResponse,
  ModuleHealthResponse,
} from '../api/types';
import type { RunHeatmapRow } from '../organisms/RunHeatmap/RunHeatmap';

// 실행 status → 히트맵/편성표 상태 문자.
// 백엔드 status는 JobStatus 외에 RETRY_SUCCESS(재시도 성공) 등이 더 올 수 있어
// string 키로 받고, 미매핑 값은 호출부에서 '_'로 처리한다.
const STATE_CHAR: Record<string, string> = {
  SUCCESS: 'S',
  RETRY_SUCCESS: 'Y',
  FAILED: 'F',
  RUNNING: 'R',
  QUEUED: 'U',
};

// 대시보드에 필요한 모든 데이터를 한 객체로. (편성표는 useSchedule이 별도 관리)
export interface DashboardData {
  kpi: DashboardKpiResponse;
  heatmap: RunHeatmapRow[];
  moduleHealth: ModuleHealthResponse;
}

export interface UseDashboardReturn {
  data: DashboardData | null;
  loading: boolean;
  error: string | null;
  retry: () => void;
}

export function useDashboard(token: string | null): UseDashboardReturn {
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 히트맵 응답(cells) → 히트맵 행으로 변환.
  // 각 셀이 이미 모듈별 최근 실행(run) 목록을 오래된→최신 순으로 제공.
  const buildHeatmapFromCells = (heatmap: HeatmapResponse): RunHeatmapRow[] => {
    // 서버가 cells를 누락/비배열로 주면 빈 히트맵으로 방어(대시보드 전체 크래시 방지).
    const cells = Array.isArray(heatmap?.cells) ? heatmap.cells : [];
    return cells
      // 실행 이력이 없는 스텁 모듈은 히트맵에서 제외
      .filter((cell) => (Array.isArray(cell.runs) ? cell.runs.length : 0) > 0)
      .map((cell) => {
        // 가장 최근 20개만 (왼쪽이 옛날, 오른쪽이 최신). 부족하면 _ 패딩.
        const recent = (Array.isArray(cell.runs) ? cell.runs : []).slice(-20);
        const runs = recent
          .map((r) => STATE_CHAR[r.status] ?? '_')
          .join('')
          .padEnd(20, '_');

        // 셀 클릭 → 상세 이동용 job id (runs와 인덱스 정렬, 패딩 셀은 null)
        const ids: (number | null)[] = recent.map((r) => r.jobId);
        while (ids.length < 20) ids.push(null);

        // dates 배열: hover 시 표시할 날짜/시간
        const dates = recent.map((r) =>
          new Date(r.startTime).toLocaleString('ko-KR', {
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
          })
        );

        // 가장 최근 실행(recent의 마지막)의 collectorType — 정렬/그룹 구분용
        const collectorType = recent.length ? recent[recent.length - 1].collectorType : undefined;

        // bad는 서버가 계산해 내려줌
        return { name: cell.name, runs, bad: cell.bad, dates, ids, collectorType };
      });
  };

  // 데이터 페칭: KPI, 히트맵, 모듈 헬스를 병렬로 호출 (총 3개 요청)
  const fetch = useCallback(async () => {
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const [kpi, heatmapDto, moduleHealth] = await Promise.all([
        endpoints.fetchDashboardKpi(),
        endpoints.fetchHeatmap(2),
        endpoints.fetchModuleHealth(),
      ]);

      const heatmap = buildHeatmapFromCells(heatmapDto);

      setData({ kpi, heatmap, moduleHealth });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load dashboard'
      );
    } finally {
      setLoading(false);
    }
  }, [token]);

  // 컴포넌트 마운트 또는 token 변경 시 페칭
  useEffect(() => {
    fetch();
  }, [fetch]);

  return { data, loading, error, retry: fetch };
}
